import { createAsyncThunk } from '@reduxjs/toolkit'
import { AMLAction, AMLUsersServerData } from '@lk/api'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { ThunkApi } from '../index.d'

export interface LoadAMLUsersAttributes {
  startDate: string
  endDate: string
  search: string
  RiskLevelID?: string | number,
  type?: AMLAction
}

export const loadAMLUnslUsers = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_UNSL_USERS',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.Unsl, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML unsl users')
  },
)

export const loadAMLZoneUsers = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_ZONE_USERS',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.Zone, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML zone users')
  },
)

export const loadAMLExternals = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_EXTERNAL_SYSTEMS',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.External, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML external systems')
  },
)

export const loadAMLAlerts = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_ALERTS',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.Alert, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML alerts')
  },
)

export const loadAMLRiskOperations = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_RISK_OPERATIONS_SYSTEMS',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.RiskOper, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML risk operations systems')
  },
)

export const loadAMLCRA = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_CRA',
  async ({ startDate, endDate, search }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.CRA, startDate, endDate, search)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML external systems')
  },
)

export interface AddToAMLJournalAttributes {
  authLevel: number
  id: number
  hash: string
}

export const addToAMLUnslJournal = createAsyncThunk<void, AddToAMLJournalAttributes, ThunkApi>(
  '[AML] ADD_TO_AML_UNSL_JOURNAL',
  async ({ authLevel, id, hash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.aml.addToJournal(AMLAction.Unsl, {
      Auth: authLevel,
      UNSLID: id,
      HashSum_UNSLID_: hash,
    })

    if (!result.success) {
      dispatch(LKModalActions.openError(result.error.message))
      return rejectWithValue('Failed to add to AML unsl journal')
    }
  },
)

export const addToAMLZoneJournal = createAsyncThunk<void, AddToAMLJournalAttributes, ThunkApi>(
  '[AML] ADD_TO_AML_ZONE_JOURNAL',
  async ({ authLevel, id, hash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.aml.addToJournal(AMLAction.Zone, {
      Auth: authLevel,
      ZoneID: id,
      HashSum_ZoneID_: hash,
    })

    if (!result.success) {
      dispatch(LKModalActions.openError(result.error.message))
      return rejectWithValue('Failed to add to AML zone journal')
    }
  },
)

export const addToAMLExternalJournal = createAsyncThunk<void, AddToAMLJournalAttributes, ThunkApi>(
  '[AML] ADD_TO_AML_EXTERNALS_JOURNAL',
  async ({ authLevel, id, hash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.aml.addToJournal(AMLAction.External, {
      Auth: authLevel,
      LogExternalID: id,
      HashSum_LogExternalID_: hash,
    })

    if (!result.success) {
      dispatch(LKModalActions.openError(result.error.message))
      return rejectWithValue('Failed to add to AML externals journal')
    }
  },
)

export const addToAMLRiskOperJournal = createAsyncThunk<void, AddToAMLJournalAttributes, ThunkApi>(
  '[AML] ADD_TO_AML_RISK_JOURNAL',
  async ({ authLevel, id, hash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.aml.addToJournal(AMLAction.RiskOper, {
      Auth: authLevel,
      AMLRiskOperID: id,
      HashSum_AMLRiskOperID_: hash,
    })

    if (!result.success) {
      dispatch(LKModalActions.openError(result.error.message))
      return rejectWithValue('Failed to add to AML externals journal')
    }
  },
)

export const loadAMLRiskLevels = createAsyncThunk<AMLUsersServerData, LoadAMLUsersAttributes, ThunkApi>(
  '[AML] LOAD_AML_RISK_LEVELS_SYSTEMS',

  async ({ startDate, endDate, search, RiskLevelID }, { extra, rejectWithValue }) => {
    const result = await extra.api.aml.getUsers(AMLAction.RiskLevels, startDate, endDate, search, RiskLevelID)

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load AML risk levels systems')
  },
)
